import { IconButton } from '@vrplatform/components';
import {
  type ResponseData,
  useApiQuery,
  useEnabledFeatures,
  useTeam,
} from '@vrplatform/data';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import {
  formatCurrency,
  hasValue,
  isUUID,
  toTitleCase,
  utc,
} from '@vrplatform/utils';
import { Box, Group, Indicator, Skeleton, Text, Tooltip } from '@mantine/core';
import {
  getJournalEntryRelation,
  type JournalEntryRelation,
} from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { isOpeningBalanceTransaction } from './_utils';
import { JournalEntryIssuesIcon } from './_components';

export type JournalEntriesRow =
  ResponseData<'get:/reports/manager-statements/detail/journal-entries'>['data'][0];

export type JournalEntriesOwnerStatementArgs = {
  currency: string | null;
  listingOwnershipPeriodId: string | null;
  startAt: string;
};

export type JournalEntriesLinkActions = {
  onGoToOpeningBalances?: () => void;
  onOpenDeposit?: (transactionId: string) => void;
  onOpenExpense?: (transactionId: string) => void;
  onOpenOwner?: (contactId: string) => void;
  onOpenOwnerStatement?: (args: JournalEntriesOwnerStatementArgs) => void;
  onOpenPayout?: (transactionId: string) => void;
  onOpenReservation?: (reservationId: string) => void;
  onOpenTransfer?: (transactionId: string) => void;
};

const TYPE_ICONS: Record<JournalEntryRelation, IconDefinition> = {
  reservation: 'CalendarEventIcon',
  recurringFee: 'PercentageCircleIcon',
  deposit: 'PlusCircleIcon',
  expense: 'MinusCircleIcon',
  transfer: 'ShuffleIcon',
  payout: 'CircleDollarIcon',
};

export const useJournalEntriesTableColumns = ({
  onGoToOpeningBalances,
  onOpenDeposit,
  onOpenExpense,
  onOpenOwner,
  onOpenOwnerStatement,
  onOpenPayout,
  onOpenReservation,
  onOpenTransfer,
}: JournalEntriesLinkActions = {}) => {
  const [{ currency }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  return useMemo(() => {
    const base: MRT_ColumnDef<JournalEntriesRow>[] = [
      {
        header: 'Type',
        Header: () => null,
        mantineTableHeadCellProps: { align: 'center' },
        mantineTableBodyCellProps: { align: 'center' },
        size: 30,
        minSize: 30,
        maxSize: 0,
        Cell: ({ row }) => {
          const relation = getJournalEntryRelation(row.original);
          const type = row.original.type;

          if (!relation) return null;

          const label = [
            relation === 'recurringFee'
              ? 'Fee & Commission'
              : toTitleCase(relation),
            toTitleCase(type),
          ]
            .filter(hasValue)
            .join(' - ');

          return (
            <Indicator
              color="orange"
              disabled={row.original.status !== 'inactive'}
              zIndex={1}
            >
              <Tooltip
                multiline
                label={
                  row.original.status === 'inactive' ? (
                    <>
                      {label}
                      <br />
                      (Deactivated - Not accounted for in the General Ledger
                      reports)
                    </>
                  ) : (
                    label
                  )
                }
                withArrow
              >
                <Icon icon={TYPE_ICONS[relation]} size={18} />
              </Tooltip>
            </Indicator>
          );
        },
      },
      {
        header: 'Txn',
        size: 110,
        maxSize: 0,
        minSize: 110,
        Cell: ({ row }) => {
          const date = row.original.txnAt
            ? utc(row.original.txnAt).format('ll')
            : '';
          const txnNum = row.original.txnCode || row.original.uniqueRef;

          return (
            <Box>
              {date && <Text>{date}</Text>}
              {txnNum && (
                <Text
                  c="gray"
                  size="xs"
                  sx={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-all',
                  }}
                >
                  {txnNum}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        header: 'Listing / Ownership',
        Cell: ({ row }) => {
          const { data, isInitialLoading } = useApiQuery(
            async (api, signal, args) => {
              if (!args.periodId || !isUUID(args.periodId))
                return { period: null };

              const period = await api.GET('/listings/ownership-periods/{id}', {
                params: {
                  path: {
                    id: args.periodId,
                  },
                },
                signal,
              });

              return {
                period: period.data,
              };
            },
            {
              queryGroup: 'reports',
              uniqueQueryKey: 'journal-entries-table-listing-ownership',
              variables: {
                periodId: row.original.listingOwnershipPeriodId,
              },
            }
          );

          const period = data?.period;

          if (isInitialLoading || data === undefined)
            return <Skeleton height={15} />;

          const getDates = () => {
            if (!period) return null;

            const hasEndAt = period.endAt;

            if (!hasEndAt)
              return [
                utc(period.startAt).format('MMM DD, YYYY'),
                'forever',
              ].join(' - ');

            const hasSameYear =
              utc(period.startAt).year() === utc(period.endAt).year();

            if (hasSameYear)
              return [
                utc(period.startAt).format('MMM DD'),
                utc(period.endAt).format('MMM DD, YYYY'),
              ].join(' - ');

            return [
              utc(period.startAt).format('MMM DD, YYYY'),
              utc(period.endAt).format('MMM DD, YYYY'),
            ].join(' - ');
          };

          const dates = getDates();
          const listingName = row.original.detail?.listing || '-';
          const owners = row.original.detail?.owners;

          if (!period?.id) return <Text size="sm">{listingName}</Text>;

          return (
            <Box>
              <Group gap={5} wrap="nowrap" mb={5}>
                <Tooltip withArrow label={dates}>
                  <Icon icon="CalendarDatesIcon" size={14} />
                </Tooltip>
                <Box>
                  <Text size="sm">{listingName}</Text>
                  {owners && (
                    <Text size="xs" c="gray">
                      {owners}
                    </Text>
                  )}
                </Box>
              </Group>
            </Box>
          );
        },
      },
      {
        header: 'Links',
        id: 'links',
        size: 70,
        minSize: 70,
        mantineTableHeadCellProps: {
          pl: 20,
        },
        maxSize: 0,
        Cell: ({ row }) => {
          const reservationId = row.original.reservationId;
          const transactionId = row.original.transactionId;
          const contactId = row.original.contactId;
          const transferOwnerStatementId = row.original.ownerStatementId;
          const shouldLoadTransaction =
            !!transactionId &&
            (!!onOpenDeposit ||
              !!onOpenExpense ||
              !!onOpenPayout ||
              !!onOpenTransfer ||
              !!onGoToOpeningBalances);
          const shouldLoadContact = !!contactId && !!onOpenOwner;
          const shouldLoadOwnerStatement =
            !!transferOwnerStatementId && !!onOpenOwnerStatement;

          const { data, isInitialLoading } = useApiQuery(
            async (api, signal, args) => {
              const [contact, transferOwnerStatement, transaction] =
                await Promise.allSettled([
                  args.contactId
                    ? api
                        .GET('/contacts/{id}', {
                          params: {
                            path: {
                              id: args.contactId,
                            },
                          },
                          signal,
                        })
                        .then((res) => {
                          if (res.error) return;
                          return res.data;
                        })
                    : null,
                  args.transferOwnerStatementId
                    ? api
                        .GET('/statements/{id}', {
                          params: {
                            path: {
                              id: args.transferOwnerStatementId,
                            },
                          },
                          signal,
                        })
                        .then((res) => {
                          if (res.error) return;
                          return res.data;
                        })
                    : null,
                  args.transactionId
                    ? api
                        .GET('/transactions/{id}', {
                          params: {
                            path: {
                              id: args.transactionId,
                            },
                          },
                          signal,
                        })
                        .then((res) => {
                          if (res.error) return;
                          return res.data;
                        })
                    : null,
                ]);

              return {
                contact: contact.status === 'fulfilled' ? contact.value : null,
                transferOwnerStatement:
                  transferOwnerStatement.status === 'fulfilled'
                    ? transferOwnerStatement.value
                    : null,
                transaction:
                  transaction.status === 'fulfilled' ? transaction.value : null,
              };
            },
            {
              queryGroup: 'reports',
              uniqueQueryKey: 'journal-entries-table-links',
              variables: {
                transactionId: shouldLoadTransaction
                  ? transactionId
                  : undefined,
                contactId: shouldLoadContact ? contactId : undefined,
                transferOwnerStatementId: shouldLoadOwnerStatement
                  ? transferOwnerStatementId
                  : undefined,
              },
            }
          );

          if (
            (shouldLoadTransaction ||
              shouldLoadContact ||
              shouldLoadOwnerStatement) &&
            (isInitialLoading || !data)
          )
            return <Skeleton height={15} />;

          const transaction = data?.transaction ?? null;
          const transferOwnerStatement = data?.transferOwnerStatement ?? null;
          const contact = data?.contact ?? null;

          const issues = row.original.issues ?? [];

          if (
            !reservationId &&
            !transaction?.id &&
            !transferOwnerStatement?.id &&
            !contact?.id &&
            !issues.length
          )
            return null;

          return (
            <Group gap={0} wrap="nowrap" justify="center" w="100%">
              {!!issues.length && <JournalEntryIssuesIcon issues={issues} />}

              {reservationId && onOpenReservation && (
                <IconButton
                  icon="CalendarEventIcon"
                  iconSize={14}
                  onClick={() => onOpenReservation(reservationId)}
                  tooltip={
                    <>
                      Reservation
                      <br />
                      {row.original.detail?.reservationConfirmationCode ||
                        reservationId.split('-')[0]}
                    </>
                  }
                />
              )}
              {contact?.id && onOpenOwner && (
                <IconButton
                  icon={!contact.companyType ? 'UserIcon' : 'OfficeIcon'}
                  iconSize={14}
                  onClick={() => {
                    if (contact.type === 'owner') onOpenOwner(contact.id);
                  }}
                  tooltip={
                    <>
                      {contact.type === 'owner' ? 'Owner' : 'Contact'}
                      <br />
                      {contact.name}
                    </>
                  }
                />
              )}
              {transaction?.id &&
                (onOpenDeposit ||
                  onOpenExpense ||
                  onOpenPayout ||
                  onOpenTransfer ||
                  onGoToOpeningBalances) && (
                  <IconButton
                    onClick={() => {
                      const openingBalance = isOpeningBalanceTransaction(
                        transaction.uniqueRef
                      );

                      if (openingBalance) {
                        if (openingBalance?.type !== 'listing') {
                          onGoToOpeningBalances?.();
                        }
                        return;
                      }

                      if (transaction.type === 'expense')
                        onOpenExpense?.(transaction.id);
                      else if (transaction.type === 'payout')
                        onOpenPayout?.(transaction.id);
                      else if (transaction.type === 'transfer')
                        onOpenTransfer?.(transaction.id);
                      else onOpenDeposit?.(transaction.id);
                    }}
                    aria-label={`Open ${toTitleCase(transaction.type)} ${transaction.uniqueRef || transaction.id.split('-')[0]}`}
                    icon={
                      transaction.type === 'expense'
                        ? 'MinusCircleIcon'
                        : transaction.type === 'payout'
                          ? 'CircleDollarIcon'
                          : transaction.type === 'transfer'
                            ? 'ShuffleIcon'
                            : 'PlusCircleIcon'
                    }
                    tooltip={
                      <>
                        {toTitleCase(transaction.type)}
                        <br />
                        {transaction.uniqueRef || transaction.id.split('-')[0]}
                      </>
                    }
                    iconSize={14}
                  />
                )}
              {transferOwnerStatement?.id &&
                transferOwnerStatement.startAt &&
                transferOwnerStatement.ownership?.id &&
                transferOwnerStatement.currency &&
                onOpenOwnerStatement && (
                  <IconButton
                    icon="ClipboardTextIcon"
                    iconSize={15}
                    onClick={() =>
                      onOpenOwnerStatement({
                        currency: transferOwnerStatement.currency ?? null,
                        listingOwnershipPeriodId:
                          transferOwnerStatement.ownership?.id ?? null,
                        startAt: transferOwnerStatement.startAt!,
                      })
                    }
                    tooltip={utc(transferOwnerStatement.startAt).format(
                      'YYYY-MM'
                    )}
                  />
                )}
            </Group>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description / Reservation Code',
        Cell: ({ row }) => {
          const description = row.original.description;
          const reservationConfirmationCode =
            row.original.detail?.reservationConfirmationCode;

          if (!description) return '-';

          return (
            <Box>
              <Text>{description}</Text>
              {reservationConfirmationCode && (
                <Text c="gray" size="xs">
                  {reservationConfirmationCode}
                </Text>
              )}
            </Box>
          );
        },
      },

      {
        size: 60,
        minSize: 60,
        maxSize: 0,
        header: 'Party',
        Header: '',
        accessorKey: 'party',
        mantineTableBodyCellProps: {
          align: 'center',
        },
        mantineTableHeadCellProps: {
          align: 'center',
        },
        Cell: ({ row }) => {
          const party = row.original.party;

          if (!party) return null;

          return (
            <Tooltip label={toTitleCase(party)} withArrow>
              <Icon
                icon={
                  party.toLowerCase() === 'owners'
                    ? 'UsersIcon'
                    : 'DashboardIcon'
                }
                size={18}
              />
            </Tooltip>
          );
        },
      },
      {
        header: 'Account',
        maxSize: 160,
        Cell: ({ row }) => {
          const account = row.original.account;
          const detail = row.original.detail;

          if (!account) return '-';

          return (
            <Box>
              <Text>{account.name}</Text>
              <Text size="xs" c="gray">
                {detail?.category
                  ? `${detail?.category}${row.original.detail?.classification ? ` (${toTitleCase(row.original.detail?.classification)})` : ''}`
                  : '-'}
              </Text>
            </Box>
          );
        },
      },
      {
        header: 'Debit',
        maxSize: 0,
        minSize: 100,
        size: 100,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const centTotal = row.original.centTotal;
          if (centTotal > 0) return formatCurrency(centTotal / 100, currency);
          return '';
        },
      },
      {
        header: 'Credit',
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        maxSize: 0,
        minSize: 100,
        size: 100,
        Cell: ({ row }) => {
          const centTotal = row.original.centTotal;
          if (centTotal < 0) return formatCurrency(centTotal / 100, currency);
          return '';
        },
      },
    ];

    if (OPEX) {
      base.splice(5, 0, {
        size: 60,
        minSize: 60,
        maxSize: 0,
        header: 'Book',
        Header: '',
        accessorKey: 'book',
        mantineTableBodyCellProps: {
          align: 'center',
        },
        mantineTableHeadCellProps: {
          align: 'center',
        },
        Cell: ({ row }) => {
          const ledger = row.original.ledger;

          if (!ledger) return null;

          return (
            <Tooltip label={toTitleCase(ledger)} withArrow>
              <Icon
                icon={ledger === 'trust' ? 'BookOpenIcon' : 'BriefcaseIcon'}
                size={18}
              />
            </Tooltip>
          );
        },
      });
    }

    return base;
  }, [
    currency,
    OPEX,
    onGoToOpeningBalances,
    onOpenDeposit,
    onOpenExpense,
    onOpenOwner,
    onOpenOwnerStatement,
    onOpenPayout,
    onOpenReservation,
    onOpenTransfer,
  ]);
};
